import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  closeMobileMenu,
  setCurrentPath,
  toggleMobileMenu,
} from "../features/navigation/navigationSlice";
import {
  fetchNotifications,
  updateNotifications,
} from "../features/admin/NotificationSlice";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { HomeIcon, UserCircleIcon } from "@heroicons/react/16/solid";
import bannerTop from "../assets/BCCU-01-min-2048x230.png";
import { convertToSlug } from "../utils/helps";
import SearchMember from "./SearchMember";
import {
  ArrowPathIcon,
  ArrowRightIcon,
  BellAlertIcon,
  BellIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  InboxIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { message } from "../utils/message";
import { toast } from "react-toastify";
import { HashLink } from "react-router-hash-link";

const useScrollPosition = () => {
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return scrollPosition;
};

const normalizePathname = (url) => {
  const pathname = url.split(/[?#]/)[0] || "/";
  const normalizedPathname = pathname.replace(/\/+$/, "");

  return normalizedPathname || "/";
};

export default function Header() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userFullName, setUserFullName] = useState("");
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const [visibleNotificationCount, setVisibleNotificationCount] = useState(20);
  const [isSimulatingRefresh, setIsSimulatingRefresh] = useState(false);
  const [simulatedRefreshKey, setSimulatedRefreshKey] = useState(0);
  const notificationLoaderRef = useRef(null);
  const simulatedRefreshTimerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    return () => {
      if (simulatedRefreshTimerRef.current) {
        window.clearTimeout(simulatedRefreshTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      dispatch(closeMobileMenu());
    }
  }, [location.pathname]);

  useEffect(() => {
    const fullName = localStorage.getItem("full_name");
    if (fullName) {
      setIsLoggedIn(true);
      setUserFullName(fullName);
    } else {
      setIsLoggedIn(false);
      setUserFullName("");
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("access_token_bccu");
    localStorage.removeItem("permissions");
    localStorage.removeItem("full_name");
    localStorage.removeItem("type_user");
    localStorage.removeItem("userId");
    localStorage.removeItem("companyId");
    localStorage.removeItem("userPhone");
    localStorage.removeItem("userEmail");
    localStorage.setItem("cart", JSON.stringify([]));
    setIsLoggedIn(false);
    setUserFullName("");
    setIsUserMenuOpen(false);
    navigate("/");
    window.location.reload();
  };

  const members = useSelector((state) => state.users?.users?.data?.users);
  const { navbars } = useSelector((state) => state.categories);
  const { notifications, status } = useSelector((state) => state.notifications);
  const { posts } = useSelector((state) => state.posts);
  const filteredMembers = members;
  const notificationList = Array.isArray(notifications) ? notifications : [];
  const unreadNotificationCount = notificationList.filter(
    (notification) => notification.isRead === "0",
  ).length;
  const displayedNotifications = notificationList.slice(0, 5);
  const visibleNotifications = notificationList.slice(
    0,
    visibleNotificationCount,
  );

  const logo = useSelector((state) => state.logo.logo);

  const dispatch = useDispatch();
  const { isMobileMenuOpen, currentPath } = useSelector(
    (state) => state.navigation,
  );
  const scrollPosition = useScrollPosition();

  const isSticky = scrollPosition > 200;

  const navItems = [
    {
      path: "/",
      label: <HomeIcon className="font-bold h-6 w-6" />,
    },
    { path: "/gioi-thieu", label: "Giới thiệu" },
    {
      path: "/hoat-dong",
      label: "Hoạt động",
      subMenu: navbars
        ?.filter((e) => e.type === "navbar")
        ?.map((item) => ({
          ...item,
          path: `/hoat-dong/${item.slug}`,
          label: item.name,
        })),
    },
    {
      path: "/tin-tuc-su-kien",
      label: "Tin tức & sự kiện",
    },
    { path: "/video", label: "Video" },
    { path: "/nha-tai-tro", label: "Nhà tài trợ" },
    {
      path: "/ban-dieu-hanh",
      label: "Ban điều hành",
      subMenu:
        isLoggedIn &&
        filteredMembers
          ?.filter((e) => e.type === "admin")
          ?.map((item) => ({
            ...item,
            path: `/thanh-vien/${convertToSlug(item.userFullName)}`,
            label: item.userFullName,
          })),
    },
    {
      path: "/thong-bao",
      label: "Thông báo",
      subMenu: navbars
        ?.filter((e) => e.type === "notification")
        ?.map((item) => ({
          ...item,
          path: `/thong-bao/${item.slug}`,
          label: item.name,
        })),
    },
    { path: "/lien-he", label: "Liên hệ" },
  ];

  const userId = localStorage.getItem("userId");

  useEffect(() => {
    if (status === "idle" && userId) {
      dispatch(fetchNotifications(userId));
    }
  }, [status, dispatch]);

  const handleReadNotification = async (id) => {
    try {
      await dispatch(updateNotifications(id)).unwrap();
    } catch (error) {
      toast.error("Lỗi khi truy cập thông báo");
    }
  };

  const handleScrollToMemberForm = () => {
    if (isMobileMenuOpen) {
      dispatch(toggleMobileMenu());
    }
    window.dispatchEvent(new Event("scrollToMemberForm"));
  };

  useEffect(() => {
    if (isMobileMenuOpen || showAllNotifications) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isMobileMenuOpen, showAllNotifications]);

  const handleUrl = (slug) => {
    const data = (posts || []).find((post) => post.slug === slug);
    if (data?.type == "notification" && data?.category?.slug) {
      return "/thong-bao/" + data.category.slug + "/" + slug;
    }

    return "/post/" + slug;
  };

  const openNotificationModal = () => {
    setVisibleNotificationCount(20);
    setShowAllNotifications(true);
  };

  const playSimulatedRefresh = () => {
    if (simulatedRefreshTimerRef.current) {
      window.clearTimeout(simulatedRefreshTimerRef.current);
    }

    setSimulatedRefreshKey((currentKey) => currentKey + 1);
    setIsSimulatingRefresh(true);
    window.scrollTo({ top: 0, behavior: "smooth" });

    simulatedRefreshTimerRef.current = window.setTimeout(() => {
      setIsSimulatingRefresh(false);
      simulatedRefreshTimerRef.current = null;
    }, 200);
  };

  const handleNotificationClick = async (event, notification) => {
    event.preventDefault();
    const targetUrl = handleUrl(notification.data);
    const currentPathname = normalizePathname(location.pathname);
    const targetPathname = normalizePathname(targetUrl);

    event.currentTarget.closest("details")?.removeAttribute("open");
    setShowAllNotifications(false);

    if (currentPathname === targetPathname) {
      playSimulatedRefresh();

      if (notification.isRead === "0") {
        await handleReadNotification(notification.id);
      }
      return;
    }

    if (notification.isRead === "0") {
      await handleReadNotification(notification.id);
    }
    navigate(targetUrl);
  };

  useEffect(() => {
    if (
      !showAllNotifications ||
      !notificationLoaderRef.current ||
      visibleNotificationCount >= notificationList.length
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleNotificationCount((currentCount) =>
            Math.min(currentCount + 20, notificationList.length),
          );
        }
      },
      { rootMargin: "120px" },
    );

    observer.observe(notificationLoaderRef.current);
    return () => observer.disconnect();
  }, [showAllNotifications, visibleNotificationCount, notificationList.length]);

  useEffect(() => {
    if (!showAllNotifications) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowAllNotifications(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [showAllNotifications]);

  const renderNotificationDropdown = () => (
    <div className="relative group flex items-center px-2">
      <button
        type="button"
        className="relative flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition hover:bg-sky-50 hover:text-sky-600 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
        aria-label="Mở thông báo"
      >
        <BellIcon className="h-6 w-6" />
        {unreadNotificationCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1 text-[11px] font-bold leading-none text-white shadow-sm ring-2 ring-white">
            {unreadNotificationCount > 99 ? "99+" : unreadNotificationCount}
          </span>
        )}
      </button>

      <div className="absolute right-0 top-full z-50 hidden w-80 max-w-[calc(100vw-2rem)] pt-3 group-hover:block group-focus-within:block sm:w-96">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_-15px_rgba(15,23,42,0.35)]">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <div>
              <p className="font-semibold text-slate-900">Thông báo</p>
              <p className="mt-0.5 text-xs text-slate-500">
                {unreadNotificationCount > 0
                  ? `${unreadNotificationCount} thông báo chưa đọc`
                  : "Bạn đã xem tất cả thông báo"}
              </p>
            </div>
            <span className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">
              {notificationList.length}
            </span>
          </div>

          <div className="max-h-72 overflow-y-auto p-2">
            {displayedNotifications.length === 0 ? (
              <div className="flex flex-col items-center px-4 py-8 text-center">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                  <InboxIcon className="h-5 w-5" />
                </span>
                <p className="mt-3 text-sm font-medium text-slate-700">
                  Chưa có thông báo
                </p>
              </div>
            ) : (
              displayedNotifications.map((notification) => {
                const isUnread = notification.isRead === "0";

                return (
                  <Link
                    key={notification.id}
                    to={handleUrl(notification.data)}
                    onClick={(event) =>
                      handleNotificationClick(event, notification)
                    }
                    className={`group/item mb-1 flex items-start gap-3 rounded-xl px-3 py-3 transition last:mb-0 hover:bg-slate-50 ${
                      isUnread ? "bg-sky-50/70" : ""
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                        isUnread
                          ? "bg-sky-100 text-sky-600"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {isUnread ? (
                        <BellAlertIcon className="h-[18px] w-[18px]" />
                      ) : (
                        <CheckCircleIcon className="h-[18px] w-[18px]" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span
                        className={`line-clamp-2 text-sm leading-5 ${
                          isUnread
                            ? "font-semibold text-slate-900"
                            : "font-medium text-slate-600"
                        }`}
                      >
                        {notification.content}
                      </span>
                      <span
                        className={`mt-1 block text-xs ${
                          isUnread ? "text-sky-600" : "text-slate-400"
                        }`}
                      >
                        {isUnread ? "Chưa đọc" : "Đã đọc"}
                      </span>
                    </span>
                    <ChevronRightIcon className="mt-2 h-4 w-4 shrink-0 text-slate-300 transition group-hover/item:translate-x-0.5 group-hover/item:text-sky-500" />
                  </Link>
                );
              })
            )}
          </div>

          <div className="border-t border-slate-100 bg-white p-2.5">
            <button
              type="button"
              onClick={openNotificationModal}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-sky-50 px-4 py-2.5 text-sm font-semibold text-sky-700 transition hover:bg-sky-100 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1"
            >
              Xem tất cả thông báo
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <header className="w-full bg-base-100 shadow-md">
      <div>
        <div className="hidden lg:flex navbar flex-col lg:flex-row-reverse py-4 container mx-auto px-4 max-w-[1200px]">
          {/* Banner */}
          <div className="relative w-full h-[100px] rounded-lg overflow-hidden">
            <img
              src={bannerTop}
              alt="Top Banner"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Logo and Mobile Menu Button */}
          <div className="flex w-full md:w-40 items-center justify-between mt-4 md:mt-0">
            <div className="w-40">
              <Link to={"/"}>
                <img
                  src={logo}
                  alt="Logo"
                  className="h-16 md:h-20 object-contain"
                />
              </Link>
            </div>
            <button
              className="btn btn-ghost lg:hidden"
              onClick={() => dispatch(toggleMobileMenu())}
            >
              {isMobileMenuOpen ? (
                <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              ) : (
                <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                  <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div
          className={`flex lg:hidden navbar flex-col lg:flex-row-reverse py-0 container mx-auto px-4 max-w-[1200px] bg-base-100 
          ${isSticky ? "fixed block w-full top-0 z-50 shadow-lg" : ""}
          `}
        >
          <div
            className={`flex w-full md:w-40 items-center justify-between md:mt-0 `}
          >
            <div className="w-40">
              <Link to={"/"}>
                <img
                  src={logo}
                  alt="Logo"
                  className="h-16 md:h-20 object-contain"
                />
              </Link>
            </div>
            <button
              className="btn btn-ghost lg:hidden"
              onClick={() => dispatch(toggleMobileMenu())}
            >
              <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
              </svg>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => dispatch(closeMobileMenu())}
          />
        )}

        <div className={`dropdown-content hidden lg:block`}>
          <ul
            className={`menu menu-horizontal w-full lg:flex lg:justify-center flex-col lg:flex-row gap-1.5 p-2 bg-base-200 transition-all duration-300 ${
              isSticky ? "lg:py-1" : ""
            }`}
          >
            {navItems.map((item) => (
              <li key={item.path} className="relative">
                {item.subMenu?.length > 0 ? (
                  <>
                    <label
                      tabIndex={0}
                      className={`font-bold btn btn-ghost justify-between whitespace-nowrap h-12 ${
                        currentPath === item.path
                          ? "text-primary font-bold"
                          : ""
                      }`}
                    >
                      {item.label}
                      <svg
                        className="w-4 h-4 ml-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </label>
                    <ul
                      tabIndex={0}
                      className="dropdown-content menu p-2 shadow-lg bg-base-100 rounded-box w-52 z-50"
                    >
                      {item?.subMenu?.map((subItem) => (
                        <li key={subItem.path}>
                          <Link
                            to={subItem.path}
                            className={`whitespace-normal ${
                              currentPath === subItem.path ? "active" : ""
                            }`}
                            onClick={() => {
                              dispatch(setCurrentPath(subItem.path));
                              // dispatch(toggleMobileMenu());
                            }}
                          >
                            {subItem.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <Link
                    to={item.path == "/ban-dieu-hanh" ? "" : item.path}
                    className={`btn btn-ghost font-bold h-12 ${
                      currentPath === item.path ? "text-primary" : ""
                    }`}
                    onClick={() => {
                      dispatch(setCurrentPath(item.path));
                      dispatch(closeMobileMenu());
                    }}
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            ))}

            <li className="relative dropdown">
              {isLoggedIn ? (
                <>
                  <label
                    tabIndex={0}
                    className="btn btn-ghost font-bold h-12 flex items-center gap-2 flex-start justify-start w-fit"
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  >
                    <UserCircleIcon className="h-5 w-5 text-gray-600" />
                    <span className="truncate max-w-[150px]">
                      {userFullName}
                    </span>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </label>
                  <ul
                    tabIndex={0}
                    className="dropdown-content menu p-2 shadow-lg bg-base-100 rounded-box w-52 z-50"
                  >
                    <li>
                      <Link
                        to={"/admin"}
                        onClick={() => {
                          setTimeout(() => {
                            window.location.reload();
                          }, 0);
                        }}
                      >
                        Trang quản trị
                      </Link>
                    </li>

                    <li>
                      <button onClick={handleLogout} className="text-error">
                        Đăng xuất
                      </button>
                    </li>
                  </ul>
                </>
              ) : (
                <Link
                  to="/login"
                  className="btn btn-ghost font-bold h-12 flex justify-start gap-2"
                  onClick={() => {
                    dispatch(setCurrentPath("/login"));
                    // dispatch(toggleMobileMenu());
                  }}
                >
                  <UserCircleIcon className="h-5 w-5 text-gray-600" />
                  Đăng nhập
                </Link>
              )}
            </li>

            {isLoggedIn && renderNotificationDropdown()}

            {!isLoggedIn && (
              <li>
                {/* <label
                  tabIndex={0}
                  className={`font-bold btn btn-ghost justify-between whitespace-nowrap h-12`}
                  onClick={handleScrollToMemberForm}
                >
                  Đăng ký
                </label> */}
                {/* <a href="/#dang-ky">Dăng ký</a> */}
                <HashLink
                  tabIndex={0}
                  className={`font-bold btn btn-ghost justify-between whitespace-nowrap h-12`}
                  onClick={() => {
                    dispatch(setCurrentPath("/"));
                  }}
                  to="/#dang-ky"
                >
                  Đăng ký
                </HashLink>
              </li>
            )}
            {isLoggedIn && <SearchMember />}
          </ul>
        </div>

        <div
          className={`fixed top-0 left-0 h-full w-3/4 bg-base-200 shadow-lg z-50 transform 
            ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"} 
            transition-transform duration-300 ease-in-out overflow-y-auto`}
        >
          <div className="absolute top-5 right-10 w-24 z-[100] text-right">
            <button
              className=" ml-2 text-2xl"
              onClick={() => dispatch(closeMobileMenu())}
            >
              ✖
            </button>
          </div>
          <ul
            className={`menu menu-horizontal w-full lg:flex lg:justify-center flex-col lg:flex-row gap-1.5 p-2 bg-base-200 transition-all duration-300 ${
              isSticky ? "lg:py-1" : ""
            }`}
          >
            {navItems.map((item) => (
              <li key={item.path} className="relative">
                {item.subMenu?.length > 0 ? (
                  <>
                    <label
                      tabIndex={0}
                      className={`font-bold btn btn-ghost justify-between whitespace-nowrap h-12 ${
                        currentPath === item.path
                          ? "text-primary font-bold"
                          : ""
                      }`}
                    >
                      {item.label}
                      <svg
                        className="w-4 h-4 ml-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </label>
                    <ul
                      tabIndex={0}
                      className="dropdown-content menu p-2 shadow-lg bg-base-100 rounded-box w-52 z-50"
                    >
                      {item?.subMenu?.map((subItem) => (
                        <li key={subItem.path}>
                          <Link
                            to={subItem.path}
                            className={`whitespace-normal ${
                              currentPath === subItem.path ? "active" : ""
                            }`}
                            onClick={() => {
                              dispatch(setCurrentPath(subItem.path));
                              dispatch(toggleMobileMenu());
                            }}
                          >
                            {subItem.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`btn btn-ghost font-bold h-12 ${
                      currentPath === item.path ? "text-primary" : ""
                    }`}
                    onClick={() => {
                      dispatch(setCurrentPath(item.path));
                      dispatch(toggleMobileMenu());
                    }}
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            ))}

            <li className="relative dropdown">
              {isLoggedIn ? (
                <>
                  <label
                    tabIndex={0}
                    className="btn btn-ghost font-bold h-12 flex items-center gap-2 flex-start justify-start w-fit"
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  >
                    <UserCircleIcon className="h-5 w-5 text-gray-600" />
                    <span className="truncate max-w-[150px]">
                      {userFullName}
                    </span>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </label>
                  <ul
                    tabIndex={0}
                    className="dropdown-content menu p-2 shadow-lg bg-base-100 rounded-box w-52 z-50"
                  >
                    <li>
                      <Link
                        to={"/admin"}
                        onClick={() => {
                          setTimeout(() => {
                            window.location.reload();
                          }, 0);
                        }}
                      >
                        Trang quản trị
                      </Link>
                    </li>

                    <li>
                      <button onClick={handleLogout} className="text-error">
                        Đăng xuất
                      </button>
                    </li>
                  </ul>
                </>
              ) : (
                <Link
                  to="/login"
                  className="btn btn-ghost font-bold h-12 flex justify-start gap-2"
                  onClick={() => {
                    dispatch(setCurrentPath("/login"));
                    dispatch(toggleMobileMenu());
                  }}
                >
                  <UserCircleIcon className="h-5 w-5 text-gray-600" />
                  Đăng nhập
                </Link>
              )}
            </li>
            {isLoggedIn && renderNotificationDropdown()}
            {!isLoggedIn && (
              <li>
                <label
                  tabIndex={0}
                  className={`font-bold btn btn-ghost justify-between whitespace-nowrap h-12`}
                  onClick={handleScrollToMemberForm}
                >
                  Đăng ký
                </label>
              </li>
            )}
            {isLoggedIn && <SearchMember />}
          </ul>
        </div>
      </div>

      {showAllNotifications && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notification-modal-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowAllNotifications(false);
            }
          }}
        >
          <div className="flex h-full w-full max-w-4xl flex-col overflow-hidden bg-slate-50 shadow-2xl sm:h-[88vh] sm:max-h-[780px] sm:rounded-3xl">
            <div className="relative border-b border-slate-200 bg-gradient-to-r from-sky-50 via-white to-white px-5 py-5 sm:px-7 sm:py-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-4">
                  <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sky-600 text-white shadow-lg shadow-sky-200 sm:flex">
                    <BellAlertIcon className="h-6 w-6" />
                  </span>
                  <div className="min-w-0">
                    <h2
                      id="notification-modal-title"
                      className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl"
                    >
                      Trung tâm thông báo
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Theo dõi những cập nhật mới nhất dành cho bạn
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm">
                        {notificationList.length} thông báo
                      </span>
                      {unreadNotificationCount > 0 && (
                        <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
                          {unreadNotificationCount} chưa đọc
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAllNotifications(false)}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-slate-300 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
                  aria-label="Đóng danh sách thông báo"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/80 px-3 py-4 sm:px-7 sm:py-6">
              {visibleNotifications.length === 0 ? (
                <div className="flex min-h-[320px] flex-col items-center justify-center px-4 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-slate-400 shadow-sm ring-1 ring-slate-200">
                    <InboxIcon className="h-8 w-8" />
                  </span>
                  <p className="mt-5 text-base font-semibold text-slate-800">
                    Chưa có thông báo
                  </p>
                  <p className="mt-1 max-w-sm text-sm leading-6 text-slate-500">
                    Những cập nhật mới sẽ xuất hiện tại đây.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {visibleNotifications.map((notification) => {
                    const isUnread = notification.isRead === "0";

                    return (
                      <Link
                        key={notification.id}
                        to={handleUrl(notification.data)}
                        onClick={(event) =>
                          handleNotificationClick(event, notification)
                        }
                        className={`group flex items-center gap-3 rounded-2xl border p-3.5 transition sm:gap-4 sm:p-4 ${
                          isUnread
                            ? "border-sky-200 bg-white shadow-sm ring-1 ring-sky-100 hover:border-sky-300 hover:shadow-md"
                            : "border-slate-200 bg-white/80 hover:border-slate-300 hover:bg-white hover:shadow-sm"
                        }`}
                      >
                        <span
                          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl sm:h-12 sm:w-12 ${
                            isUnread
                              ? "bg-sky-100 text-sky-600"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {isUnread ? (
                            <BellAlertIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                          ) : (
                            <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span
                            className={`block text-sm leading-6 sm:text-[15px] ${
                              isUnread
                                ? "font-semibold text-slate-900"
                                : "font-medium text-slate-600"
                            }`}
                          >
                            {notification.content}
                          </span>
                          <span
                            className={`mt-1.5 inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                              isUnread
                                ? "bg-sky-50 text-sky-700"
                                : "bg-slate-100 text-slate-500"
                            }`}
                          >
                            {isUnread ? "Chưa đọc" : "Đã đọc"}
                          </span>
                        </span>
                        <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-sky-500" />
                      </Link>
                    );
                  })}
                </div>
              )}

              {visibleNotificationCount < notificationList.length && (
                <div
                  ref={notificationLoaderRef}
                  className="flex items-center justify-center gap-2 py-6 text-sm font-medium text-slate-500"
                >
                  <ArrowPathIcon className="h-4 w-4 animate-spin text-sky-500" />
                  Đang tải thêm...
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t border-slate-200 bg-white px-5 py-3 text-xs text-slate-500 sm:px-7">
              <span>
                Đang hiển thị {visibleNotifications.length} trên tổng số{" "}
                {notificationList.length}
              </span>
              {unreadNotificationCount === 0 && (
                <span className="inline-flex items-center gap-1.5 font-medium text-emerald-600">
                  <CheckCircleIcon className="h-4 w-4" />
                  Đã xem tất cả
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {isSimulatingRefresh &&
        createPortal(
          <div
            key={simulatedRefreshKey}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/70 backdrop-blur-[2px]"
            role="status"
            aria-label="Đang làm mới nội dung"
          >
            <span className="absolute inset-x-0 top-0 h-1 overflow-hidden bg-sky-100">
              <span className="block h-full w-1/2 animate-pulse rounded-r-full bg-sky-500" />
            </span>
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-sky-100 bg-white shadow-lg shadow-sky-100/80 sm:h-16 sm:w-16">
              <ArrowPathIcon className="h-8 w-8 animate-spin text-sky-600 sm:h-9 sm:w-9" />
            </span>
          </div>,
          document.body,
        )}
    </header>
  );
}
